<template>
  <div>
    <c-page-section squares data-testid="1on1-record">
      <q-card>
        <q-card-section class="header">
          <div class="row items-center no-wrap">
            <div class="col">
              <div class="heading-bigger">
                {{ props.data.collaborator.name }}
                <q-icon v-if="props.data.isFinancialMessage" color="amber-8" name="savings" size="md" class="q-mr-sm">
                  <q-tooltip>
                    {{ t('user1on1s.dialog.isFinancialMessage') }}
                  </q-tooltip>
                </q-icon>
                <span class="text-overline">{{
                  formatDateForTemplate({
                    date: props.data.date,
                  })
                }}</span>
              </div>
            </div>
            <div class="col col-auto">
              <q-icon
                v-if="allowEdit"
                data-testid="edit-1on1"
                name="edit"
                color="grey"
                class="cursor-pointer q-pr-xs"
                size="sm"
                @click="clickEdit(data)"
              >
                <q-tooltip>
                  {{ t('user1on1s.tooltip.edit') }}
                </q-tooltip>
              </q-icon>
              <q-icon
                v-if="allowDelete"
                data-testid="delete-1on1"
                color="grey"
                name="delete_outline"
                size="sm"
                class="cursor-pointer"
                @click="clickDelete(data.uuid)"
              >
                <q-tooltip>
                  {{ t('user1on1s.tooltip.delete') }}
                </q-tooltip>
              </q-icon>
            </div>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="row">
          <div class="col-sm-12 col-md-7 q-pr-lg">
            <wysiwyg-editor-v2
              ref="editor"
              :editable="false"
              height="100%"
              :smaller-headers="true"
              @ready="onEditorReady"
            />
          </div>

          <div class="col-sm-12 col-md-5">
            <div class="q-mb-md">
              <rating-panel
                :title="t('user1on1s.userRating.title')"
                :description="t('user1on1s.userRating.description')"
                :bad="props.data.collaboratorRating === hubClient.Collaborator1On1Rating.BAD"
                :ok="props.data.collaboratorRating === hubClient.Collaborator1On1Rating.OK"
                :great="props.data.collaboratorRating === hubClient.Collaborator1On1Rating.GREAT"
                :align="'row'"
                data-testid="user-feeling"
              />
            </div>
            <div class="q-mb-md">
              <rating-panel
                :title="t('user1on1s.companyRating.title')"
                :description="t('user1on1s.companyRating.description')"
                :bad="props.data.companyRating === hubClient.Collaborator1On1Rating.BAD"
                :ok="props.data.companyRating === hubClient.Collaborator1On1Rating.OK"
                :great="props.data.companyRating === hubClient.Collaborator1On1Rating.GREAT"
                :align="'row'"
                data-testid="company-rating"
              />
            </div>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section class="footer">
          <last-change
            :date="
              formatDateForTemplate({
                date: props.data.updatedAt,
                showTime: true,
              })
            "
            :editor-name="props.data.editor.name"
          />
        </q-card-section>
      </q-card>

      <q-separator />
    </c-page-section>
  </div>
</template>

<script lang="ts" setup>
import CPageSection from '@/components/_common/PageSection.vue';
import { hubClient } from '@tfpl/api-client';
import { useI18n } from 'vue-i18n';
import { formatDateForTemplate } from '@/utils';
import RatingPanel from '@/components/_common/RatingPanel.vue';
import LastChange from '@/components/_common/LastChange.vue';
import WysiwygEditorV2 from '@/components/_common/WysiwygEditorV2.vue';
import { ref } from 'vue';
const { t } = useI18n();

/*****************************************************************************
 * PROPS
 ****************************************************************************/
const props = defineProps<{
  data: hubClient.Collaborator1On1Dto;
  allowEdit: boolean;
  allowDelete: boolean;
}>();

/*****************************************************************************
 * EMITS
 ****************************************************************************/
const emit = defineEmits<{
  (e: 'click:delete', uuid: string): void;
  (e: 'click:edit', onOneOn: hubClient.Collaborator1On1Dto): void;
}>();
const clickDelete = (uuid: string) => {
  emit('click:delete', uuid);
};

const clickEdit = (onOneOn: hubClient.Collaborator1On1Dto) => {
  emit('click:edit', onOneOn);
};
/*****************************************************************************
 * SETUP
 ****************************************************************************/

const editor = ref();

const onEditorReady = () => {
  editor.value.setHTML(props.data.message);
};
</script>
<style scoped lang="scss">
@import 'styles/app.variables.scss';
</style>
